import React from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import {COUPON_STATUS, DISCOUNT_TYPE, VISIBILITY_TYPE} from '@/pages/Coupon/constant';
import { ModalHeader, ModalBody, ModalFooter } from '@/components/common/Modal/types';
import Button from '@/components/common/Button';
import Input from '@/components/common/Input';
import { ReactComponent as InformationIcon } from '@/assets/icons/ri-information-line.svg'
import { ReactComponent as RotateIcon } from '@/assets/icons/ri-rotate.svg'
import { convertToLocalTime } from '@/utils';
import {GENERAL_STATUS_STYLE} from "@/constant/status";
import { CircularLoading } from '@/components/common/CircularLoading';

export const renderCouponFormHeader: ModalHeader = {
	title: 'Add/Edit Coupon'
}


const generateCode = () : String => {
	let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < 10) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
      counter += 1;
    }
	return result
}

export const renderCouponFormBody = (
	{ methods },
	couponId,
	startDate,
	endDate,
	venues,
	submitModal,
	showDiscountForm,
	setStartDate,
	setEndDate
): ModalBody => ({
	children: (
		<FormProvider {...methods}>
			<form onSubmit={methods.handleSubmit(submitModal)} className='flex flex-col items-start pt-3 px-8 gap-4'>
				<Input expanded name='couponTitle' type='text' label='Coupon Title'
							 placeholder='Fill Coupon Title' {...methods.register('couponTitle')} />
				<div className='flex justify-end w-full gap-2 relative content-evenly'>
					<Input expanded name='couponCode' type='text' label='Coupon Code'
						placeholder='Fill or Generate Code' {...methods.register('couponCode')} />
					<RotateIcon className='group absolute bottom-0 w-12 mb-2 hover:cursor-pointer' 
						onClick={() => methods.setValue('couponCode', generateCode())}/>
				</div>
				<Input expanded name='usageQuota' type='number' label='Usage Quota' {...methods.register('usageQuota')} />
				<div className='flex flex-col w-full gap-2'>
					<p className='text-jsm'>Visibility Type</p>
					<Controller
						name='visibility'
						render={({ field }) =>
							<Select
								className='w-full'
								options={VISIBILITY_TYPE}
								placeholder='Choose Visibility on Customer App'
								isClearable
								{...methods.register('visibility')}
								{...field}
							/>}
					/>
				</div>
				<div className='flex flex-col w-full gap-2'>
					<p className='text-jsm'>Discount Type</p>
					<Controller
						name='discountType'
						render={({ field }) =>
							<Select
								className='w-full'
								options={DISCOUNT_TYPE}
								placeholder='Choose Discount Type'
								isClearable
								{...methods.register('discountType')}
								{...field}
							/>}
					/>
				</div>
				<Input name='discountPercent' type='number' label='Discount Amount' placeholder='Enter discount amount'
							 className='w-full' {...methods.register('discountPercent')} />
				{showDiscountForm &&
				<Input name='maxDiscount' type='number' label='Max Discount Value' placeholder='Enter Max Discount Value'
							 className='w-full' {...methods.register('maxDiscount')} />}
				<Input expanded name='minPurchase' type='number' label='Min. Purchase'
							 placeholder='Minimum Purchase' {...methods.register('minPurchase')} />
				<div className='flex justify-between w-full gap-3'>
					<div className='flex flex-col w-full gap-2'>
						<p className='text-jsm'>Start Date</p>
						<DatePicker
							selected={new Date(convertToLocalTime(startDate) || methods.getValues('startDate'))}
							onChange={(date) => {
								const newStart = date.getTime();
								setStartDate(newStart);
							}}
						/>
					</div>
					<div className='flex flex-col w-full gap-2'>
						<p className='text-jsm'>End Date</p>
						<DatePicker
							selected={new Date(convertToLocalTime(endDate) || methods.getValues('endDate'))}
							onChange={(date) => {
								const newEnd = date.getTime();
								setEndDate(newEnd);
							}}
						/>
					</div>
				</div>
				<div className='flex flex-col w-full gap-2'>
					<p className='text-jsm'>Venue</p>
					<Controller
						name='venue'
						render={({ field }) =>
							<Select
								options={
									venues.map(venue => ({
										label: venue.name,
										value: venue.id,
									}))
								}
								placeholder='Select venue'
								isClearable
								{...methods.register('venue')}
								{...field}
							/>}
						/>
				</div>
				<div className='flex items-start gap-3 py-4 border-t border-t-grey-line-subtle border-b border-b-grey-line-subtle w-full'>
					<div className='flex justify-between items-center w-full gap-2'>
						<div className='flex p-0 gap-1 items-center'>
							<InformationIcon/>
							<p className='font-medium text-sm'>Status</p>
						</div>
						<Controller
							name='status'
							render={({ field }) =>
								<select
									placeholder='Select status'
									className={`color-select ${GENERAL_STATUS_STYLE[methods.watch('status')]}`}
									{...field}
									{...methods.register('status')}
								>
									{
										COUPON_STATUS.map(i =>
											<option
												key={i}
												value={i}
												className={GENERAL_STATUS_STYLE[i]}
												
											>
												{i}
											</option>
										)
									}
								</select>
							}
						/>
					</div>
				</div>
			</form>
		</FormProvider>
	),
	data: { value: { id: couponId } }
})

export const renderCouponFormFooter = (closeModal, submitModal, isLoading) : ModalFooter => ({
	children: (
		<div className="flex justify-between items-center w-full p-8 gap-3">
			<Button type="button" variant="basic" onClick={closeModal()}>Cancel</Button>
			<Button type="submit" variant="primary" onClick={ isLoading || submitModal()}>
				{isLoading ? 
					<CircularLoading /> :
					'Save'
				}
			</Button>
		</div>
	)
})
